import { useQuery } from '@tanstack/react-query';

import { getProjectKpis } from '@/features/projects/project-details/api/get-project-kpis';
import { supabase } from '@/lib/supabase';
import { ProjectKpiWithMetrics, ProjectType } from '@/shared/types/api';

export interface LastTrackerOrg {
  id: number;
  name: string | null;
  image_url: string | null;
}

export type KpiWithLastTracker = ProjectKpiWithMetrics & {
  lastTrackerOrg: LastTrackerOrg | null;
};

async function fetchLastTrackerOrg(
  kpiId: number,
): Promise<LastTrackerOrg | null> {
  // Step 1: Get the latest tracking entry (project_id + created_by)
  const { data: entry, error: entryError } = await supabase
    .from('kpi_tracking_entries')
    .select('id, created_at, project_id')
    .eq('kpi_id', kpiId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (entryError || !entry) return null;

  // Step 2: Try to get the org through the project (works when user has access)
  const { data: projectData } = await supabase
    .from('projects')
    .select('org:orgs!projects_org_id_fkey(id, name, image_url)')
    .eq('id', entry.project_id)
    .maybeSingle();

  if (projectData?.org) {
    const org = projectData.org as unknown as LastTrackerOrg;
    return { id: org.id, name: org.name, image_url: org.image_url };
  }

  // Step 3: Fallback – the client may not have RLS access to the contractor's
  // subproject. Look up the org via project_contractors → partner_aliases instead.
  const { data: kpiData } = await supabase
    .from('project_kpis')
    .select('project_id')
    .eq('id', kpiId)
    .maybeSingle();

  if (kpiData?.project_id) {
    const { data: contractors } = await supabase
      .from('project_contractors')
      .select('contractor_org_id:partner_aliases!inner(partner_orga_id)')
      .eq('project_id', kpiData.project_id)
      .limit(1);

    if (contractors && contractors.length > 0) {
      const partnerOrgId = (contractors[0] as any).contractor_org_id
        ?.partner_orga_id;
      if (partnerOrgId) {
        const { data: orgData } = await supabase
          .from('orgs')
          .select('id, name, image_url')
          .eq('id', partnerOrgId)
          .maybeSingle();
        if (orgData) return orgData;
      }
    }
  }

  return null;
}

export function useKpisWithExistingEntries({
  projectId,
  projectType,
}: {
  projectId: number;
  projectType: ProjectType;
}) {
  return useQuery({
    queryKey: ['kpis-with-existing-entries', projectId, projectType],
    queryFn: async (): Promise<KpiWithLastTracker[]> => {
      const kpis = await getProjectKpis(projectId, projectType);
      const filteredKpis = kpis.filter(
        (kpi) => kpi.metrics?.latest_entry_value != null,
      );

      const kpisWithLastTracker = await Promise.all(
        filteredKpis.map(async (kpi) => {
          const lastTrackerOrg = await fetchLastTrackerOrg(kpi.id);
          return {
            ...kpi,
            lastTrackerOrg,
          };
        }),
      );

      return kpisWithLastTracker;
    },
  });
}
